import {
    Component,
    ChangeDetectionStrategy,
    Input,
    TemplateRef,
    EventEmitter,
    Output,
    inject,
} from '@angular/core';
import { of, shareReplay, startWith, tap } from 'rxjs';
import { map } from 'rxjs/operators';
import { InfiniteListComponent } from '@bazis/shared/components/infinite-list.component';
import { MassSelectedService } from '@pages/shared/services/mass-selected.service';
import { SHARE_REPLAY_SETTINGS } from '@bazis/configuration.service';

@Component({
    selector: 'app-multiple-selection-infinite-list',
    templateUrl: './multiple-selection-infinite-list.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class MultipleSelectionInfiniteListComponent extends InfiniteListComponent {
    readonly massSrv = inject(MassSelectedService, { optional: true });

    @Input() valuesMap: Map<any, boolean>;

    @Input() infiniteListTpl: TemplateRef<any>;

    @Output() toggled = new EventEmitter();

    isSelectedAll$ = this.massSrv
        ? this.massSrv.selectedAll.valueChanges.pipe(
              startWith(this.massSrv.selectedAll.value),
              map((value) => !!value?.length),
              shareReplay(SHARE_REPLAY_SETTINGS),
          )
        : of(false);

    isPartialSelection$ = this.massSrv ? this.massSrv.isPartialSelection$ : of(false);

    visibleList$ = of([]);

    override ngOnInit(): void {
        super.ngOnInit();

        this.visibleList$ = this.list$.pipe(
            tap((list) => {
                if (!this.massSrv) {
                    return;
                }

                this.massSrv.updateForm(
                    list.map((item) => this.getItemId(item)).filter((id) => !!id),
                );
            }),
            shareReplay(SHARE_REPLAY_SETTINGS),
        );
    }

    selectAll(list) {
        list.forEach((item) => {
            const id = this.getItemId(item);

            if (id && !this.valuesMap.has(id)) {
                this.toggleSelectedValue(id);
            }
        });
    }

    unSelectAll(list) {
        list.forEach((item) => {
            const id = this.getItemId(item);

            if (id) {
                this.toggleSelectedValue(id);
            }
        });
    }

    toggleAll(list = []) {
        if (this.massSrv) {
            if (this.massSrv.selectedAll.value?.length > 0) {
                this.resetCurrentSelection(null, list);
                return;
            }

            this.massSrv.selectAll();
            return;
        }

        if (this.valuesMap.size === this.countListAll) {
            this.unSelectAll(list);
            return;
        }

        this.selectAll(list);
    }

    resetCurrentSelection(event?: Event, list = []) {
        event?.stopPropagation();

        if (this.massSrv) {
            this.massSrv.selectedAll.setValue([]);
            return;
        }

        this.unSelectAll(list);
    }

    toggleSelectedValue(e) {
        this.toggled.emit(e);
    }

    getItemId(item) {
        if (!item) {
            return null;
        }

        if (this.searchSettings?.fieldForId) {
            return item.$snapshot?.[this.searchSettings.fieldForId]?.id || null;
        }

        return item.id || null;
    }
}
